import { Box, SxProps, Theme } from '@mui/material';

export type OneTimeModalDotsProps = Readonly<{
  totalSlides: number;
  currentIndex: number;
  onDotClick: (index: number) => void;
}>;

export const OneTimeModalDots = ({
  totalSlides,
  currentIndex,
  onDotClick,
}: OneTimeModalDotsProps) => {
  if (totalSlides <= 1) {
    return null;
  }

  return (
    <Box sx={containerSx} role="tablist" aria-label="Slide navigation">
      {Array.from({ length: totalSlides }, (_, index) => {
        const isCurrent = index === currentIndex;
        const isClickable = index < currentIndex;

        return (
          <Box
            id={`one-time-modal-dot-${index}`}
            key={index}
            sx={dotSx(isCurrent, isClickable)}
            onClick={() => {
              if (isClickable) {
                onDotClick(index);
              }
            }}
            role="tab"
            tabIndex={isClickable ? 0 : -1}
            onKeyDown={(e) => {
              if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                onDotClick(index);
              }
            }}
            aria-label={`Slide ${index + 1} of ${totalSlides}${isCurrent ? ' (current)' : ''}`}
            aria-selected={isCurrent}
          />
        );
      })}
    </Box>
  );
};

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  gap: backrs.spacing['spacing-xl'],
});

const dotSx =
  (isCurrent: boolean, isClickable: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    width: '10px',
    height: '10px',
    borderRadius: backrs.radius['radius-full'],
    backgroundColor: isCurrent
      ? backrs.colors['Colors/Foreground/fg-brand-primary-alt']
      : backrs.colors['Colors/Background/bg-quaternary'],
    cursor: isClickable ? 'pointer' : 'default',
    transition: 'background-color 0.2s ease',
    '&:hover': isClickable
      ? {
          backgroundColor: backrs.colors['Colors/Background/bg-tertiary'],
        }
      : {},
    '&:focus-visible': {
      outline: `2px solid ${backrs.colors['Colors/Foreground/fg-brand-primary-alt']}`,
      outlineOffset: '2px',
    },
  });
